import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AiBomLine {
  bom_material_id: number;
  material_id: number;
  material_code?: string;
  material_name?: string;
  unit?: string;
  category?: string;
  quantity: number;
  remarks?: string;
  saved_rate: number;
  saved_amount: number;
  current_rate: number;
  current_amount: number;
  delta: number;
  delta_pct: number;
  source: string;
  as_of?: string | null;
}

export interface AiBomQuote {
  bom: {
    bom_id: number;
    bom_code: string;
    description?: string;
    version: number;
    status: string;
    project_id: number;
    project_name?: string;
  };
  lines: AiBomLine[];
  totals: {
    saved_total: number;
    current_total: number;
    delta: number;
  };
  quoted_at: string;
}

export interface AiMaterialRate {
  id: number;
  material_id: number;
  org_id: number | null;
  unit_rate: number;
  source: string;
  region?: string | null;
  as_of?: string | null;
  material_code?: string;
  material_name?: string;
  unit?: string;
  catalog_rate?: number | null;
  owned: boolean;
}

@Injectable({ providedIn: 'root' })
export class AiBomService {
  private baseUrl = `${environment.BASE_URL}ai-bom`;

  constructor(private http: HttpClient) {}

  quote(bomId: number): Observable<{ status: string; data: AiBomQuote }> {
    return this.http.post<{ status: string; data: AiBomQuote }>(`${this.baseUrl}/${bomId}/quote`, {});
  }

  apply(bomId: number, bomMaterialIds?: number[]): Observable<{ status: string; message?: string; data: AiBomQuote }> {
    return this.http.post<{ status: string; message?: string; data: AiBomQuote }>(
      `${this.baseUrl}/${bomId}/apply`,
      { bom_material_ids: bomMaterialIds }
    );
  }

  saveAsVersion(bomId: number, bomMaterialIds?: number[]): Observable<{
    status: string;
    message?: string;
    data: { bom_id: number; bom_code: string; version: number; status: string };
  }> {
    return this.http.post<{
      status: string;
      message?: string;
      data: { bom_id: number; bom_code: string; version: number; status: string };
    }>(`${this.baseUrl}/${bomId}/save-as-version`, { bom_material_ids: bomMaterialIds });
  }

  listRates(): Observable<{ status: string; data: AiMaterialRate[] }> {
    return this.http.get<{ status: string; data: AiMaterialRate[] }>(`${this.baseUrl}/rates`);
  }

  saveRate(payload: {
    material_id: number;
    unit_rate: number;
    source?: string;
    region?: string;
    as_of?: string;
  }): Observable<{ status: string; data: Partial<AiMaterialRate> }> {
    return this.http.post<{ status: string; data: Partial<AiMaterialRate> }>(`${this.baseUrl}/rates`, payload);
  }

  deleteRate(id: number): Observable<{ status: string; message?: string }> {
    return this.http.delete<{ status: string; message?: string }>(`${this.baseUrl}/rates/${id}`);
  }
}
